import React, { useCallback, useMemo } from "react";

import { useIntl } from "react-intl";

import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { SelectField } from "@mapmycustomers/ui";

import Divider from "@app/component/FormFields/components/Divider";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import getLayoutModelByEntityType from "@app/util/layout/impl";
import itemToValueLabel from "@app/util/ui/itemToValueLabel";

interface Props {
  dividerClassName?: string;
  entityType: EntityTypesSupportingFieldCustomization;
  onChange?: (layout: FormLayout) => void;
  value: FormLayout;
}

const FormLayoutSelector: React.FC<Props> = ({ dividerClassName, entityType, onChange, value }) => {
  const intl = useIntl();

  const options = useMemo(
    () => getLayoutModelByEntityType(entityType).layouts.map(itemToValueLabel),
    [entityType]
  );

  const handleChange = useCallback(
    (layoutId) => {
      const layout = getLayoutModelByEntityType(entityType).getById(layoutId);
      if (layout && onChange) {
        onChange(layout);
      }
    },
    [entityType, onChange]
  );

  if (!options.length) {
    return null;
  }

  return (
    <>
      <SelectField<FormLayout["id"]>
        dropdownMatchSelectWidth={false}
        filterOption={defaultFilterOption}
        label={intl.formatMessage({
          id: "forms.formLayoutSelector",
          defaultMessage: "Select Layout",
          description: "Layout selector label",
        })}
        onChange={handleChange}
        options={options}
        required
        showSearch
        value={value?.id}
      />
      <Divider className={dividerClassName} />
    </>
  );
};

export default FormLayoutSelector;
